import { create } from 'zustand';
import { Session, Chatter, ChatterStats } from '../types';
import { api } from '../services/api';
import toast from 'react-hot-toast';

interface SessionState {
  sessions: Session[];
  currentSession: Session | null;
  chatters: Chatter[];
  stats: ChatterStats;
  isLoading: boolean;
  
  // Actions
  fetchSessions: () => Promise<void>;
  createSession: (name: string, keywords?: string) => Promise<Session>;
  updateSession: (id: string, data: Partial<Session>) => Promise<void>;
  deleteSession: (id: string) => Promise<void>;
  setCurrentSession: (session: Session | null) => void;
  
  fetchChatters: (sessionId: string) => Promise<void>;
  toggleChatterGreeted: (chatterId: string, isGreeted: boolean) => Promise<void>;
  addChatter: (chatter: Chatter) => void;
  updateChatter: (chatter: Chatter) => void;
  
  calculateStats: () => void;
}

export const useSessionStore = create<SessionState>((set, get) => ({
  sessions: [],
  currentSession: null,
  chatters: [],
  stats: {
    total: 0,
    greeted: 0,
    pending: 0,
    greetedPercentage: 0,
  },
  isLoading: false,

  fetchSessions: async () => {
    try {
      set({ isLoading: true });
      const response = await api.get('/api/sessions');
      set({ sessions: response.data, isLoading: false });
    } catch (error) {
      console.error('Error fetching sessions:', error);
      toast.error('Failed to fetch sessions');
      set({ isLoading: false });
    }
  },

  createSession: async (name: string, keywords?: string) => {
    try {
      const response = await api.post('/api/sessions', { name, keywords });
      const newSession = response.data;
      set(state => ({
        sessions: [newSession, ...state.sessions],
        currentSession: newSession,
        chatters: [],
      }));
      get().calculateStats();
      // Don't show toast here since the component will show a more specific message
      return newSession;
    } catch (error) {
      console.error('Error creating session:', error);
      toast.error('Failed to create session');
      throw error;
    }
  },

  updateSession: async (id: string, data: Partial<Session>) => {
    try {
      const response = await api.put(`/api/sessions/${id}`, data);
      const updatedSession = response.data;
      
      set(state => ({
        sessions: state.sessions.map(s => s.id === id ? updatedSession : s),
        currentSession: state.currentSession?.id === id ? updatedSession : state.currentSession,
      }));
      
      toast.success('Session updated successfully!');
    } catch (error) {
      console.error('Error updating session:', error);
      toast.error('Failed to update session');
    }
  },

  deleteSession: async (id: string) => {
    try {
      await api.delete(`/api/sessions/${id}`);
      set(state => ({
        sessions: state.sessions.filter(s => s.id !== id),
        currentSession: state.currentSession?.id === id ? null : state.currentSession,
        chatters: state.currentSession?.id === id ? [] : state.chatters,
      }));
      get().calculateStats();
      toast.success('Session deleted successfully!');
    } catch (error) {
      console.error('Error deleting session:', error);
      toast.error('Failed to delete session');
    }
  },

  setCurrentSession: (session: Session | null) => {
    set({ currentSession: session, chatters: session?.chatters || [] });
    get().calculateStats();
    if (session) {
      get().fetchChatters(session.id);
    }
  },

  fetchChatters: async (sessionId: string) => {
    try {
      const response = await api.get(`/api/sessions/${sessionId}/chatters`);
      set({ chatters: response.data });
      get().calculateStats();
    } catch (error) {
      console.error('Error fetching chatters:', error);
      toast.error('Failed to fetch chatters');
    }
  },

  toggleChatterGreeted: async (chatterId: string, isGreeted: boolean) => {
    try {
      const response = await api.put(`/api/chatters/${chatterId}/greet`, { isGreeted });
      const updatedChatter = response.data;
      
      set(state => ({
        chatters: state.chatters.map(c => c.id === chatterId ? updatedChatter : c),
      }));
      
      get().calculateStats();
      toast.success(isGreeted ? 'Marked as greeted!' : 'Unmarked as greeted');
    } catch (error) {
      console.error('Error updating chatter:', error);
      toast.error('Failed to update chatter');
    }
  },

  addChatter: (chatter: Chatter) => {
    set(state => {
      const exists = state.chatters.find(c => c.id === chatter.id);
      if (exists) return state;
      
      // Add new chatter and sort by firstSeenAt to maintain FIFO order
      const newChatters = [...state.chatters, chatter].sort(
        (a, b) => new Date(a.firstSeenAt).getTime() - new Date(b.firstSeenAt).getTime()
      );
      return { chatters: newChatters };
    });
    get().calculateStats();
  },

  updateChatter: (chatter: Chatter) => {

    set(state => ({
      chatters: state.chatters.map(c => c.id === chatter.id ? chatter : c),
    }));
    get().calculateStats();
  },

  calculateStats: () => {
    const { chatters } = get();
    const total = chatters.length;
    const greeted = chatters.filter(c => c.isGreeted).length;
    const pending = total - greeted;
    const greetedPercentage = total > 0 ? Math.round((greeted / total) * 100) : 0;

    set({
      stats: {
        total,
        greeted,
        pending,
        greetedPercentage,
      },
    });
  },
})); 